import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Trash2, KeyRound, ShieldCheck, PenLine } from 'lucide-react';
import { API, inputCls } from '@/lib/api';
import { authHeaders, apiError } from '@/admin/AdminContentContext';

const ACTIONS = { save_draft: 'حفظ مسودة', publish: 'نشر المحتوى', discard_draft: 'تجاهل المسودة', restore_revision: 'استرجاع نسخة', upload_media: 'رفع ملف', delete_media: 'حذف ملف', create_user: 'إنشاء مستخدم', update_user: 'تعديل مستخدم', delete_user: 'حذف مستخدم' };
const EMPTY = { name: '', email: '', password: '', role: 'editor' };

export default function UsersPage({ user }) {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    axios.get(`${API}/admin/users`, authHeaders()).then(({ data }) => setUsers(data)),
    axios.get(`${API}/admin/audit`, authHeaders()).then(({ data }) => setLogs(data)),
  ]).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error('يرجى تعبئة جميع الحقول'); return; }
    setBusy(true);
    try { await axios.post(`${API}/admin/users`, form, authHeaders()); toast.success('تم إنشاء المستخدم'); setForm(EMPTY); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const setRole = async (u, role) => {
    try { await axios.patch(`${API}/admin/users/${u.id}`, { role }, authHeaders()); toast.success('تم تحديث الصلاحية'); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const resetPw = async (u) => {
    const password = window.prompt(`كلمة مرور جديدة للمستخدم ${u.email} (8 أحرف على الأقل):`);
    if (!password) return;
    try { await axios.patch(`${API}/admin/users/${u.id}`, { password }, authHeaders()); toast.success('تم تغيير كلمة المرور'); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`حذف المستخدم ${u.email}؟`)) return;
    try { await axios.delete(`${API}/admin/users/${u.id}`, authHeaders()); toast.success('تم الحذف'); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div><h2 className="font-display text-lg font-extrabold text-brand-ink">المستخدمون والصلاحيات</h2>
        <p className="text-xs text-brand-steel">مدير النظام: كل الصلاحيات بما فيها النشر وإدارة المستخدمين. محرر المحتوى: تعديل المحتوى وحفظه كمسودة، رفع الوسائط، وإدارة الرسائل.</p></div>

      <div className="grid gap-5 lg:grid-cols-3">
        <form onSubmit={create} className="rounded-3xl border border-brand-line bg-white p-6" data-testid="user-create-form">
          <h3 className="flex items-center gap-2 font-display text-base font-extrabold text-brand-ink"><UserPlus className="h-4 w-4 text-brand-blue" /> إضافة مستخدم</h3>
          <div className="mt-4 space-y-3">
            <input placeholder="الاسم" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className={inputCls} />
            <input placeholder="البريد الإلكتروني" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className={inputCls} />
            <input placeholder="كلمة المرور (8+ أحرف)" type="password" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className={inputCls} />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select" className={inputCls}>
              <option value="editor">محرر محتوى</option><option value="admin">مدير النظام</option>
            </select>
          </div>
          <button type="submit" disabled={busy} data-testid="user-create-btn" className="mt-4 w-full rounded-full bg-brand-blue py-3 text-sm font-bold text-white hover:bg-brand-blueHover disabled:opacity-60">إنشاء</button>
        </form>

        <div className="rounded-3xl border border-brand-line bg-white p-6 lg:col-span-2" data-testid="users-list">
          <h3 className="font-display text-base font-extrabold text-brand-ink">الحسابات ({users.length})</h3>
          <ul className="mt-4 divide-y divide-brand-line">
            {users.map((u, i) => (
              <li key={u.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`user-row-${i}`}>
                <span className={`grid h-9 w-9 place-items-center rounded-xl ${u.role === 'admin' ? 'bg-brand-blue/10 text-brand-blue' : 'bg-brand-teal/10 text-brand-teal'}`}>{u.role === 'admin' ? <ShieldCheck className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}</span>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-brand-ink">{u.name} {u.id === user.id && <span className="font-mono text-[10px] text-brand-dim">(أنت)</span>}</p><p className="truncate font-mono text-[11px] text-brand-steel" dir="ltr">{u.email}</p></div>
                <select value={u.role} onChange={(e) => setRole(u, e.target.value)} disabled={u.id === user.id} data-testid={`user-role-${i}`} className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs font-bold text-brand-ink outline-none disabled:opacity-60">
                  <option value="admin">مدير النظام</option><option value="editor">محرر محتوى</option>
                </select>
                <button onClick={() => resetPw(u)} title="تغيير كلمة المرور" data-testid={`user-reset-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-brand-steel hover:bg-brand-paper hover:text-brand-blue"><KeyRound className="h-4 w-4" /></button>
                <button onClick={() => remove(u)} disabled={u.id === user.id} title="حذف" data-testid={`user-delete-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-brand-dim hover:bg-brand-crimson/10 hover:text-brand-crimson disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="rounded-3xl border border-brand-line bg-white p-6" data-testid="audit-log">
        <h3 className="font-display text-base font-extrabold text-brand-ink">سجل التعديلات</h3>
        <p className="text-xs text-brand-steel">من عدّل المحتوى ومتى — آخر 150 عملية.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-brand-line font-mono text-[10px] uppercase tracking-widest text-brand-dim"><th className="px-3 py-2 text-start">التاريخ</th><th className="px-3 py-2 text-start">المستخدم</th><th className="px-3 py-2 text-start">العملية</th><th className="px-3 py-2 text-start">التفاصيل</th></tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan="4" className="px-3 py-10 text-center text-brand-dim" data-testid="audit-empty">لا توجد عمليات مسجلة بعد.</td></tr>}
              {logs.map((l, i) => (
                <tr key={l.id} className="border-b border-brand-line/60" data-testid={`audit-row-${i}`}>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-brand-dim" dir="ltr">{new Date(l.at).toLocaleString('en-GB')}</td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-brand-steel" dir="ltr">{l.user_email}</td>
                  <td className="px-3 py-2.5 font-bold text-brand-ink">{ACTIONS[l.action] || l.action}</td>
                  <td className="px-3 py-2.5 text-xs text-brand-steel" dir="ltr">{[l.target, l.detail].filter(Boolean).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
